import React, { ChangeEvent, useContext, useEffect, useState } from 'react'

import { AcornsIcon } from '@acorns/icons'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation } from '@apollo/react-hooks'
import { compose } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import Nav from 'components/Nav'
import OptionToggle from 'components/OptionToggle'
import Separator from 'components/Separator'
import platform from 'components/Trackers/platform'
import { ActionType, PageName, RoundUpAccount } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import trackEvent from 'utils/analytics/trackEvent'
import { IErrorModalProps, withApolloErrorModal } from 'utils/Apollo'
import { sendToSentry } from 'utils/errors'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getFlow } from 'utils/FlowProvider/utils'
import { FunnelContext } from 'utils/FunnelContext'
import { getGeneralError } from 'utils/getGeneralError'
import withTieredRegistration, { WithTieredRegistrationProps } from 'utils/TieredRegistration'

import updateRoundupsAccountMutation from './gql/mutation.updateRoundupAccount.gql'
import {
  ActionWrapper,
  Container,
  FormRowList,
  FormRowListItem,
  Label,
  StyledBody,
  StyledBodyLeftAlign,
  StyledButton,
  StyledFormContainer,
  StyledHeader,
  StyledLayout,
} from './styled-components'
import { useFormRows } from './useFormRows'

type Props = {
  handleNextClick: () => void
  header: string
  text: string
} & HeroTextProps &
  IErrorModalProps &
  WithTieredRegistrationProps &
  WithFlowRouterProps

type UpdateRoundUpAccountInput = {
  enabled: boolean
  linkedSubaccountId: string
}

type UpdateRoundUpAccountResult = {
  roundUpAccount: RoundUpAccount
}

const RoundUpsFundingScreenViewedEvent = {
  feature: 'Registration: Round-Ups Funding Screen',
  event_description: 'Screen Viewed',
  platform,
  object: 'Screen',
  action: 'Viewed',
  object_name: 'registrationRoundUpsFunding',
  style: 'main',
  screen: 'registrationRoundUpsFunding',
  screen_name: 'registrationRoundUpsFunding',
}

const NextButtonTappedEvent = {
  feature: 'Registration: Round-Ups Funding',
  event_description: 'Next button tapped',
  platform,
  object: 'Button',
  action: 'Tapped',
  object_name: 'registrationRoundUpsFunding',
  screen: 'registrationRoundUpsFunding',
  screen_name: 'registrationRoundUpsFunding',
}

const enhance = compose<Props, {}>(
  withApolloErrorModal,
  withHeroText,
  withFlowRouter,
  withTieredRegistration,
)

const RoundupsFunding: React.FunctionComponent<Props> = ({
  closeErrorModal,
  heroText,
  route,
  showErrorModal,
}) => {
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const [updateRoundupsAccount] = useMutation<
    UpdateRoundUpAccountResult,
    UpdateRoundUpAccountInput
  >(updateRoundupsAccountMutation)
  const { formRowData, loading } = useFormRows()
  const [formOptions, setFormOptions] = useState([])

  const { getRemoteString, isLoadingRemoteStrings } = useRemoteStrings()

  useEffect(() => {
    trackEvent('Screen Viewed', {
      eventData: RoundUpsFundingScreenViewedEvent,
      flow: getFlow(),
    })
  }, [])

  useEffect(() => {
    if (!loading) {
      const results = formRowData.linkedAccounts?.map(account => {
        const associatedRoundUpAccount = formRowData.roundUpAccounts?.find(
          curr => curr?.linkedSubaccountId === account.id,
        )

        // @note if a linkedAccount does not have an associated RoundupAccount,
        // default the current linkedSubaccount to roundUpsEnabled = true,
        // otherwise use the `enabled` property in the associated RoundUp Account to determine if roundUps are enabled
        const accountEnabled = associatedRoundUpAccount && associatedRoundUpAccount?.enabled
        return {
          name: account.name,
          id: account.id,
          value: accountEnabled,
        }
      })

      setFormOptions(results)
    }
  }, [loading])

  const handleSubmit = () => {
    trackEvent('Button Tapped', {
      eventData: NextButtonTappedEvent,
      flow: getFlow(),
    })
    isDynamicFunnelEnabled
      ? routeToPage(PageName.RoundUpsFunding, ActionType.Continue)
      : route({ from: FlowRoute.RoundUpsFunding, action: 'continue' })
  }
  const handleToggleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const element = event.target as HTMLInputElement

    const target = formOptions
    const index = formOptions?.findIndex(option => option.name === element.name)

    try {
      const input: UpdateRoundUpAccountInput = {
        enabled: element?.checked,
        linkedSubaccountId: target[index]?.id,
      }

      target[index].value = element?.checked
      await updateRoundupsAccount({ variables: input })
    } catch (error) {
      const retryHandler = () => {
        closeErrorModal()
      }
      const { title, message, actions, image } = getGeneralError(
        error,
        { closeErrorModal },
        retryHandler,
      )
      showErrorModal({ title, message, actions, image })
      sendToSentry(error)

      // @note if failure set target toggle back to original value
      target[index].value = !element?.checked
    }
    setFormOptions(target)
  }

  const headerText =
    getRemoteString({ key: 'core.round_ups.linked_accounts.header' }) ?? heroText.header
  const nextButtonText =
    getRemoteString({ key: 'invest.onboarding.round_ups_interstitial.cta.next' }) ??
    heroText.ctaLabel

  if (loading || isLoadingRemoteStrings) {
    return <CenteredSpinner />
  }

  return (
    <>
      <Nav back={isDynamicFunnelEnabled ? isBackEnabled(PageName.RoundUpsFunding) : true} />
      <StyledLayout>
        <Container>
          <StyledBody>{heroText.text}</StyledBody>
          <StyledFormContainer>
            <StyledHeader>{headerText}</StyledHeader>
            <StyledBodyLeftAlign>{heroText.linkedAccountsText}</StyledBodyLeftAlign>
            <FormRowList>
              {formOptions?.map(option => (
                <FormRowListItem key={option.id}>
                  <AcornsIcon icon={AcornsIcon.Icon.UtilityBank} height={24} />
                  <Label>{option.name}</Label>
                  <OptionToggle
                    data-testId={option.name.replace(' ', '_').toLowerCase()}
                    id={option.id}
                    icons={null}
                    checked={option.value}
                    onChange={handleToggleChange}
                    name={option.name}
                  />
                </FormRowListItem>
              ))}
            </FormRowList>
          </StyledFormContainer>
          <Separator />
          <ActionWrapper>
            <StyledButton
              data-testid={TestIDs.ROUNDUPS_FUNDING.NEXT_BUTTON}
              disabled={loading}
              onPress={handleSubmit}
            >
              {nextButtonText}
            </StyledButton>
          </ActionWrapper>
        </Container>
      </StyledLayout>
    </>
  )
}

export default enhance(RoundupsFunding)
